<template>
    <section class="sc-card sc-list-card">
        <!-- 검색 / 필터 / 액션 -->
        <div class="sc-toolbar">
            <label class="sc-search">
                <svg
                    width="15"
                    height="15"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                >
                    <circle cx="7" cy="7" r="4.5" />
                    <path d="M10.5 10.5L14 14" />
                </svg>
                <input
                    v-model.trim="keyword"
                    type="text"
                    :placeholder="$t('스케줄명 검색')"
                    :aria-label="$t('스케줄명 검색')"
                />
            </label>
            <select v-model="zoneFilter" class="sc-select" :aria-label="$t('제어 구역')">
                <option value="">{{ $t("구역: 전체") }}</option>
                <option v-for="zone in zoneOptions" :key="zone.value" :value="zone.value">{{ zone.text }}</option>
            </select>
            <select v-model="typeFilter" class="sc-select" :aria-label="$t('설비 유형')">
                <option value="">{{ $t("설비 유형: 전체") }}</option>
                <option v-for="type in typeOptions" :key="type.value" :value="type.value">{{ type.text }}</option>
            </select>
            <span class="sc-divider"></span>
            <button type="button" class="sc-btn" @click="$emit('except-batch', checkedRows)">
                {{ $t("예외일 설정") }}
            </button>
            <button type="button" class="sc-btn sc-btn--danger" @click="$emit('delete', checkedRows)">
                {{ $t("삭제") }}
            </button>
            <button type="button" class="sc-btn" @click="$emit('status', checkedRows, 'Y')">{{ $t("사용") }}</button>
            <button type="button" class="sc-btn" @click="$emit('status', checkedRows, 'N')">{{ $t("미사용") }}</button>
            <button type="button" class="sc-btn sc-btn--primary" @click="$emit('add')">
                <svg
                    width="13"
                    height="13"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.2"
                    stroke-linecap="round"
                >
                    <path d="M8 3v10M3 8h10" />
                </svg>
                {{ $t("추가") }}
            </button>
        </div>

        <div class="sc-table-scroll">
            <div class="sc-table">
                <div class="sc-row sc-row--head" role="row">
                    <span>
                        <input
                            type="checkbox"
                            class="sc-check"
                            :checked="allChecked"
                            :aria-label="$t('전체 선택')"
                            @change="toggleAll($event.target.checked)"
                        />
                    </span>
                    <span>{{ $t("스케줄명") }}</span>
                    <span>{{ $t("제어 구역") }}</span>
                    <span>{{ $t("제어 대상") }}</span>
                    <span>{{ $t("제어 요일") }}</span>
                    <span>{{ $t("제어 스케줄") }}</span>
                    <span>{{ $t("유효 기간") }}</span>
                    <span>{{ $t("마지막 실행 시간") }}</span>
                    <span>{{ $t("사용") }}</span>
                </div>

                <div v-if="filteredRows.length === 0" class="sc-list-empty">
                    {{
                        rows.length === 0 ? $t("등록된 제어 스케줄이 없습니다.") : $t("조건에 맞는 스케줄이 없습니다.")
                    }}
                </div>

                <div
                    v-for="row in filteredRows"
                    :key="row.schdCtrlIdx"
                    class="sc-row sc-row--body"
                    :class="{ 'is-selected': row.schdCtrlIdx === selectedIdx, 'is-disabled': row.enabled !== 'Y' }"
                    role="row"
                    tabindex="0"
                    @click="$emit('select', row)"
                    @keydown.enter.self="$emit('select', row)"
                >
                    <span @click.stop>
                        <input
                            type="checkbox"
                            class="sc-check"
                            :checked="checkedIds.includes(row.schdCtrlIdx)"
                            :aria-label="`${row.schdCtrlName} ${$t('선택')}`"
                            @change="toggleOne(row.schdCtrlIdx, $event.target.checked)"
                        />
                    </span>
                    <div>
                        <div class="sc-cell-name">{{ row.schdCtrlName }}</div>
                    </div>
                    <div class="sc-cell-text">{{ zoneLabel(row.zoneIdx) }}</div>
                    <div class="sc-cell-text">
                        <div v-for="(type, i) in row.equipTypes || []" :key="i">
                            {{ type.equiptTypeCodeName || "-" }} ({{ type.cnt ?? "-" }})
                        </div>
                    </div>
                    <div class="sc-cell-days">
                        <span
                            v-for="day in DAYS"
                            :key="day.key"
                            class="sc-day-chip"
                            :class="{ on: !!(row.repeatConfig || {})[day.key] }"
                        >
                            {{ day.label }}
                        </span>
                    </div>
                    <div class="sc-cell-events">
                        <div v-for="(event, i) in (row.events || []).slice(0, 3)" :key="i" class="sc-event-line">
                            <span class="sc-time-badge">{{ hhmm(event.schdHour) }}</span>
                            <span>{{ event.schdEventName }}</span>
                        </div>
                        <span v-if="(row.events || []).length > 3" class="sc-meta">+{{ row.events.length - 3 }}</span>
                    </div>
                    <div class="sc-cell-small">
                        {{ formatDate(row.startDate) }}<br />~ {{ formatDate(row.endDate) }}
                    </div>
                    <div class="sc-cell-run">
                        <div>{{ row.lastCtrlDate ? formatDate(row.lastCtrlDate, "MM-DD HH:mm:ss") : "-" }}</div>
                        <div>
                            <span class="ok">{{ $t("성공") }} {{ row.lastSuccessCnt ?? 0 }}</span>
                            &nbsp;
                            <span class="fail">{{ $t("실패") }} {{ row.lastFailCnt ?? 0 }}</span>
                        </div>
                    </div>
                    <div style="display: flex; justify-content: center" @click.stop>
                        <button
                            type="button"
                            role="switch"
                            class="sc-switch"
                            :class="{ 'is-on': row.enabled === 'Y' }"
                            :aria-checked="String(row.enabled === 'Y')"
                            :aria-label="`${row.schdCtrlName} ${$t('사용 여부')}`"
                            @click="$emit('toggle', row, row.enabled === 'Y' ? 'N' : 'Y')"
                        ></button>
                    </div>
                </div>
            </div>
        </div>

        <div class="sc-list-foot">
            <span
                >{{ $t("사용") }} {{ enabledCount }} · {{ $t("미사용") }} {{ filteredRows.length - enabledCount }}</span
            >
        </div>
    </section>
</template>

<script>
import { DAYS, hhmm, formatDate, zoneName } from "../utils/scheduleFormat";

export default {
    props: {
        rows: { type: Array, default: () => [] },
        selectedIdx: { type: [Number, String], default: null },
    },
    data() {
        return {
            DAYS,
            keyword: "",
            zoneFilter: "",
            typeFilter: "",
            checkedIds: [],
        };
    },
    computed: {
        zoneOptions() {
            return this.$store.state.zoneInfo || [];
        },
        typeOptions() {
            return this.$store.state.transEquipType || [];
        },
        filteredRows() {
            const keyword = this.keyword.toLowerCase();
            const typeText = (this.typeOptions.find((t) => t.value == this.typeFilter) || {}).text;
            return this.rows.filter((row) => {
                if (
                    keyword &&
                    !String(row.schdCtrlName || "")
                        .toLowerCase()
                        .includes(keyword)
                )
                    return false;
                if (this.zoneFilter !== "" && row.zoneIdx != this.zoneFilter) return false;
                if (this.typeFilter !== "") {
                    const hit = (row.equipTypes || []).some(
                        (t) => t.equipType == this.typeFilter || (typeText && t.equiptTypeCodeName === typeText),
                    );
                    if (!hit) return false;
                }
                return true;
            });
        },
        enabledCount() {
            return this.filteredRows.filter((row) => row.enabled === "Y").length;
        },
        allChecked() {
            return (
                this.filteredRows.length > 0 && this.filteredRows.every((r) => this.checkedIds.includes(r.schdCtrlIdx))
            );
        },
        checkedRows() {
            return this.rows.filter((row) => this.checkedIds.includes(row.schdCtrlIdx));
        },
    },
    watch: {
        // 목록이 새로 로드되면 사라진 행의 체크 상태를 정리
        rows(rows) {
            const ids = new Set(rows.map((r) => r.schdCtrlIdx));
            this.checkedIds = this.checkedIds.filter((id) => ids.has(id));
        },
    },
    methods: {
        hhmm,
        formatDate,
        zoneLabel(zoneIdx) {
            return zoneName(this.$store, zoneIdx);
        },
        toggleOne(id, checked) {
            this.checkedIds = checked ? [...this.checkedIds, id] : this.checkedIds.filter((x) => x !== id);
        },
        toggleAll(checked) {
            const visible = this.filteredRows.map((r) => r.schdCtrlIdx);
            this.checkedIds = checked
                ? [...new Set([...this.checkedIds, ...visible])]
                : this.checkedIds.filter((id) => !visible.includes(id));
        },
        clearChecked() {
            this.checkedIds = [];
        },
    },
};
</script>
